import { Calendar, Inbox, TrendingUp, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { PageShell } from "@/components/layout/PageShell";
import { SEO } from "@/lib/seo";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AvailabilityDot } from "@/components/AvailabilityDot";
import { useCity } from "@/contexts/CityContext";
import { useAuth } from "@/contexts/AuthContext";

const INCOMING = [
  { id: "rx1", gp: "Dr Lena Kovač", reason: "Chest pain, abnormal ECG", priority: "urgent" as const, receivedAgo: "1h" },
  { id: "rx2", gp: "Dr Sam Weller", reason: "Intermittent palpitations", priority: "routine" as const, receivedAgo: "4h" },
];

export default function SpecialistDashboard() {
  const { city, link } = useCity();
  const { user } = useAuth();

  return (
    <PageShell>
      <SEO
        title={`Specialist dashboard · ${city.name} · PulseBoard`}
        description="Manage your availability, incoming referrals, and profile."
        path={`/${city.slug}/specialist-dashboard`}
        noindex
      />

      <section className="border-b border-pb-border/60">
        <div className="mx-auto max-w-6xl px-4 py-12 md:px-8">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-pb-primary">
            Specialist dashboard · {city.name}
          </p>
          <h1 className="mt-3 font-display text-4xl font-semibold tracking-tight md:text-5xl">
            {user?.name ?? "Welcome back"}.
          </h1>
          <div className="mt-4 flex items-center gap-3">
            <AvailabilityDot state="available" withLabel />
            <span className="text-pb-ink-3">·</span>
            <Link
              to={link("/specialist/onboarding")}
              className="text-xs font-medium uppercase tracking-wider text-pb-primary hover:underline"
            >
              Change status
            </Link>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4 py-10 md:px-8">
        {/* KPI strip */}
        <div className="mb-8 grid grid-cols-2 gap-4 md:grid-cols-4">
          <Kpi Icon={Inbox} label="Pending" value={INCOMING.length} />
          <Kpi Icon={Calendar} label="This week" value={12} />
          <Kpi Icon={CheckCircle} label="Ack rate (30d)" value="94%" />
          <Kpi Icon={TrendingUp} label="Avg ack time" value="3.2h" />
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {/* Incoming referrals — takes 2/3 */}
          <div className="md:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Incoming referrals</CardTitle>
                <CardDescription>
                  Metadata shown by default. Full clinical detail on acknowledge.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <ul className="divide-y divide-pb-border">
                  {INCOMING.map((r, i) => (
                    <li
                      key={r.id}
                      className="px-6 py-5 transition-colors hover:bg-pb-surface-2/50 animate-pb-fade-up"
                      style={{ animationDelay: `${i * 60}ms` }}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-pb-ink">
                              {r.reason}
                            </p>
                            {r.priority === "urgent" && (
                              <Badge variant="urgent">Urgent</Badge>
                            )}
                          </div>
                          <p className="mt-1 text-sm text-pb-ink-3">
                            From {r.gp} · {r.receivedAgo} ago
                          </p>
                        </div>
                        <div className="flex shrink-0 gap-2">
                          <Button variant="outline" size="sm">
                            Decline
                          </Button>
                          <Button size="sm">Acknowledge</Button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Availability sidebar */}
          <aside>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Availability</CardTitle>
                <CardDescription>
                  Google Calendar sync is primary.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="rounded-md border border-pb-border bg-pb-surface-2/50 px-3 py-2 text-sm">
                  <p className="text-pb-ink-3">Next open window</p>
                  <p className="mt-1 font-medium text-pb-ink">
                    Wed 11:30 — 12:00
                  </p>
                </div>
                <Button variant="outline" size="sm" className="w-full" asChild>
                  <Link to={link("/specialist/onboarding")}>
                    Complete profile onboarding
                  </Link>
                </Button>
                <Button variant="ghost" size="sm" className="w-full">
                  Add ad-hoc window
                </Button>
              </CardContent>
            </Card>
          </aside>
        </div>
      </div>
    </PageShell>
  );
}

function Kpi({
  Icon,
  label,
  value,
}: {
  Icon: React.ElementType;
  label: string;
  value: string | number;
}) {
  return (
    <Card className="p-5">
      <div className="flex items-center gap-2 text-pb-ink-3">
        <Icon className="h-4 w-4" aria-hidden />
        <span className="text-xs font-medium uppercase tracking-wider">
          {label}
        </span>
      </div>
      <p className="mt-3 font-display text-3xl font-semibold text-pb-ink">
        {value}
      </p>
    </Card>
  );
}
